import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Cloud, CreditCard, Save, Download, UserPlus, Trash2, Edit, Upload, X, LayoutDashboard, CheckCircle2, Circle, AlertCircle, Plus, Calendar, Video, ExternalLink, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import MemberInviteModal from "@/components/MemberInviteModal";
import { EditMemberDialog } from "@/components/EditMemberDialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useGoogleIntegration } from "@/hooks/useGoogleIntegration";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import googleMeetLogo from "@/assets/google-meet-logo.png";
import googleDriveLogo from "@/assets/google-drive-logo.png";
import { DocumentsTabContent } from "@/components/DocumentsTabContent";
import { FAQManager } from "@/components/FAQManager";
import { SocialLinksManager } from "@/components/SocialLinksManager";

export default function PTAAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isTrialExpired, daysUntilDeletion, loading: trialLoading, state: trialState, daysRemaining } = useTrialStatus();
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [hasPta, setHasPta] = useState(false);
  const [threeWordPhrase, setThreeWordPhrase] = useState<string>('');
  const [sponsorshipData, setSponsorshipData] = useState<any>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState<any>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState("");
  const [userFullName, setUserFullName] = useState<string>('');
  const [invoices, setInvoices] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("summary");
  const [checklistHidden, setChecklistHidden] = useState(false);
  const [pricingPlans, setPricingPlans] = useState<any[]>([]);
  const [planFeatures, setPlanFeatures] = useState<Record<string, string[]>>({});
  const [savingPlan, setSavingPlan] = useState(false);
  const [localSelectedPlan, setLocalSelectedPlan] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Update active tab when trial status is determined
  useEffect(() => {
    if (!trialLoading && isTrialExpired) {
      setActiveTab("subscription");
    }
  }, [trialLoading, isTrialExpired]);
  
  // Fundraising portal settings
  const [showCurrentGoals, setShowCurrentGoals] = useState(true);
  const [allowPublicSubmissions, setAllowPublicSubmissions] = useState(true);
  const [showPtaMembers, setShowPtaMembers] = useState(true);
  const [showContactDetails, setShowContactDetails] = useState(true);
  const [aboutText, setAboutText] = useState('');
  const [volunteerText, setVolunteerText] = useState('');
  const [heroWelcomeText, setHeroWelcomeText] = useState('');
  const [heroDescriptionText, setHeroDescriptionText] = useState('');
  const [showMarketplaceSection, setShowMarketplaceSection] = useState(true);
  const [showMeetTeamSection, setShowMeetTeamSection] = useState(true);
  
  // Google integration
  const { 
    integration: googleIntegration, 
    loading: googleLoading, 
    connecting: googleConnecting,
    isConnected: googleConnected,
    isCalendarEnabled: googleCalendarEnabled,
    isMeetEnabled: googleMeetEnabled,
    isDriveEnabled: googleDriveEnabled,
    googleEmail,
    connectGoogle,
    disconnectGoogle,
    toggleCalendarEnabled,
    toggleMeetEnabled,
    toggleDriveEnabled,
    refetch: refetchGoogleIntegration
  } = useGoogleIntegration(ptaData?.id || null);

  // Handle Google OAuth callback
  useEffect(() => {
    const googleStatus = searchParams.get('google');
    const tab = searchParams.get('tab');
    
    if (googleStatus === 'success') {
      // If redirected to documents tab, auto-enable Drive
      if (tab === 'documents') {
        toast({
          title: "Google Drive Connected",
          description: "Your Google Drive has been successfully connected.",
        });
        refetchGoogleIntegration().then(() => {
          // Auto-enable Drive when connecting from documents tab
          if (!googleDriveEnabled) {
            toggleDriveEnabled(true);
          }
        });
        navigate('/pta-admin?tab=documents', { replace: true });
      } else {
        toast({
          title: "Google Calendar Connected",
          description: "Your Google Calendar has been successfully connected.",
        });
        refetchGoogleIntegration();
        navigate('/pta-admin?tab=integrations', { replace: true });
      }
    } else if (googleStatus === 'error') {
      const message = searchParams.get('message');
      toast({
        title: "Connection Failed",
        description: message || "Failed to connect Google. Please try again.",
        variant: "destructive",
      });
      navigate(`/pta-admin?tab=${tab || 'integrations'}`, { replace: true });
    }
  }, [searchParams, googleDriveEnabled]);

  // Handle tab param from URL for direct navigation
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && !searchParams.get('google')) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    // Always fetch pricing plans - they're public
    fetchPricingPlans();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      fetchSubscriptionData();
      checkPTAAndRedirect();
    }
  }, [user]);

  const fetchPricingPlans = async () => {
    // Fetch pricing plans (exclude test plans)
    const { data: plans, error } = await supabase
      .from('pricing_plans')
      .select('*')
      .order('display_order', { ascending: true });
    
    if (error) {
      console.error('Error fetching pricing plans:', error);
      return;
    }
    
    if (plans && plans.length > 0) {
      // Filter out test plans
      const validPlans = plans.filter(p => p.name.toLowerCase() !== 'test plan');
      setPricingPlans(validPlans);
      
      // Fetch features for each plan
      const planIds = validPlans.map(p => p.id);
      if (planIds.length > 0) {
        const { data: features } = await supabase
          .from('plan_features')
          .select('*')
          .in('plan_id', planIds)
          .order('feature_order', { ascending: true });
        
        if (features) {
          const featuresByPlan: Record<string, string[]> = {};
          features.forEach(f => {
            if (!featuresByPlan[f.plan_id]) {
              featuresByPlan[f.plan_id] = [];
            }
            featuresByPlan[f.plan_id].push(f.feature_name);
          });
          setPlanFeatures(featuresByPlan);
        }
      }
    }
  };

  const handleSelectPlan = async (planName: string) => {
    if (!ptaData?.id) return;
    
    // Set local state immediately for UI feedback
    setLocalSelectedPlan(planName.toLowerCase());
    
    setSavingPlan(true);
    try {
      // Update the subscription's plan_selected (not plan - that's set on payment)
      const { error } = await supabase
        .from('subscriptions')
        .update({ 
          plan_selected: planName.toLowerCase()
        })
        .eq('pta_id', ptaData.id);
      
      if (error) throw error;
      
      // Refresh subscription data
      await fetchSubscriptionData();
      
      toast({
        title: "Plan Selected",
        description: `You've selected the ${planName} plan. Proceed to payment to activate.`,
      });
    } catch (error: any) {
      // Revert local state on error
      setLocalSelectedPlan(null);
      toast({
        title: "Error",
        description: error.message || "Failed to select plan",
        variant: "destructive",
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleCompletePayment = async () => {
    // Get the selected plan - either from local state or database
    const selectedPlanName = localSelectedPlan || subscriptionData?.plan_selected?.toLowerCase();
    
    if (!selectedPlanName) {
      toast({
        title: "No Plan Selected",
        description: "Please select a plan before proceeding to payment.",
        variant: "destructive",
      });
      return;
    }

    // Find the plan ID from pricingPlans
    const selectedPlan = pricingPlans.find(p => p.name.toLowerCase() === selectedPlanName);
    if (!selectedPlan) {
      toast({
        title: "Plan Not Found",
        description: "Could not find the selected plan. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setProcessingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-pricing-checkout', {
        body: { planId: selectedPlan.id, ptaId: ptaData?.id },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      toast({
        title: "Payment Error",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const checkPTAAndRedirect = async () => {
    if (!user?.id) return;

    // First check by user_id
    const { data: memberData } = await supabase
      .from("members")
      .select("id, pta_id, is_pta_admin")
      .eq("user_id", user.id)
      .limit(1)
      .maybeSingle();

    // If found with user_id
    if (memberData?.pta_id && memberData?.is_pta_admin) {
      setHasPta(true);
      fetchPTAData();
      fetchMembers();
      fetchStripeAccount();
      fetchInvoices();
      return;
    }

    // If not found by user_id, check by email (for existing members not yet linked)
    if (!memberData && user.email) {
      const { data: emailMember } = await supabase
        .from("members")
        .select("id, pta_id, is_pta_admin")
        .eq("email", user.email)
        .limit(1)
        .maybeSingle();

      // If found by email and has a PTA, link it to the user
      if (emailMember?.pta_id) {
        const { error: updateError } = await supabase
          .from("members")
          .update({ user_id: user.id })
          .eq("id", emailMember.id);

        if (updateError) {
          console.error("Error linking member:", updateError);
          toast({
            title: "Error",
            description: "Failed to link your account. Please contact support.",
            variant: "destructive",
          });
          return;
        }

        // Successfully linked, check if admin
        if (emailMember.is_pta_admin) {
          setHasPta(true);
          fetchPTAData();
          fetchMembers();
          fetchStripeAccount();
          fetchInvoices();
          return;
        }
      }
    }

    // No PTA found or not an admin, redirect to onboarding
    navigate("/onboarding");
  };

  const fetchUserProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user?.id)
      .single();
    
    if (data?.full_name) {
      setUserFullName(data.full_name);
    }
  };

  const fetchInvoices = async () => {
    // Get user's PTA ID first
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();
    
    if (!memberData?.pta_id) {
      setInvoices([]);
      return;
    }

    // Fetch real invoices
    const { data } = await supabase
      .from('invoices')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .order('created_at', { ascending: false });
    
    setInvoices(data || []);
  };
  const fetchMembers = async () => {
    // Get current user's PTA
    const {
      data: userData
    } = await supabase.from("members").select("pta_id").eq("user_id", user?.id).eq("is_pta_admin", true).single();
    if (!userData?.pta_id) {
      setMembers([]);
      return;
    }

    // Fetch all members for this PTA with their roles from user_roles table
    const {
      data: membersData
    } = await supabase.from("members").select(`
        id,
        user_id,
        full_name,
        email,
        role,
        is_pta_admin,
        forum_access_disabled,
        admin_notes
      `).eq("pta_id", userData.pta_id).order("full_name");

    if (!membersData) {
      setMembers([]);
      return;
    }

    // Fetch user_roles for all members
    const userIds = membersData.map(m => m.user_id).filter(Boolean);
    const { data: rolesData } = await supabase
      .from("user_roles")
      .select("user_id, role")
      .in("user_id", userIds);

    // Merge roles data with members data
    const membersWithRoles = membersData.map(member => ({
      ...member,
      user_roles: rolesData?.filter(r => r.user_id === member.user_id) || []
    }));

    setMembers(membersWithRoles);
  };
  const fetchSubscriptionData = async () => {
    // First get user's PTA ID
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .maybeSingle();
    
    if (!memberData?.pta_id) {
      setSubscriptionData(null);
      return;
    }
    
    // Fetch PTA's subscription
    const { data } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('pta_id', memberData.pta_id)
      .maybeSingle();
    
    setSubscriptionData(data);
  };
  const fetchPTAData = async () => {
    // Get current user's PTA
    const {
      data: userData
    } = await supabase.from("members").select("pta_id").eq("user_id", user?.id).eq("is_pta_admin", true).limit(1).maybeSingle();
    if (!userData?.pta_id) {
      setHasPta(false);
      return;
    }
    
    setHasPta(true);
    const {
      data
    } = await supabase.from('ptas').select('*').eq('id', userData.pta_id).single();
    if (data) {
      setPtaData(data);
      
      // Load fundraising portal settings
      setShowCurrentGoals(data.show_current_goals ?? true);
      setAllowPublicSubmissions(data.allow_public_submissions ?? true);
      setShowPtaMembers(data.show_pta_members ?? true);
      setShowContactDetails(data.show_contact_details ?? true);
      setAboutText(data.about_text || '');
      setVolunteerText(data.volunteer_text || '');
      setHeroWelcomeText(data.hero_welcome_text || '');
      setHeroDescriptionText(data.hero_description_text || '');
      setShowMarketplaceSection(data.show_marketplace_section ?? true);
      setShowMeetTeamSection(data.show_meet_team_section ?? true);

      // Ensure sponsorship code exists, generate if missing
      if (!data.sponsorship_code || data.sponsorship_code === '000000') {
        const newCode = Math.floor(100000 + Math.random() * 900000).toString();
        await supabase.from('ptas').update({
          sponsorship_code: newCode
        }).eq('id', data.id);
        setPtaData({
          ...data,
          sponsorship_code: newCode
        });
      }

      // Ensure 3-word phrase exists
      if (!data.three_word_phrase || data.three_word_phrase === 'example-word-code') {
        const words = ['happy', 'sunny', 'bright', 'cheerful', 'joyful', 'peaceful', 'lovely', 'gentle', 'rainbow', 'sunshine', 'butterfly', 'garden', 'meadow', 'forest', 'ocean', 'mountain'];
        const randomWords = Array.from({
          length: 3
        }, () => words[Math.floor(Math.random() * words.length)]);
        const newPhrase = randomWords.join('-');
        await supabase.from('ptas').update({
          three_word_phrase: newPhrase
        }).eq('id', data.id);
        setThreeWordPhrase(newPhrase);
      } else {
        setThreeWordPhrase(data.three_word_phrase);
      }
      setLogoPreview(data.logo_url);
      setLogoRemoved(false);

      // Fetch sponsorship data after PTA data is loaded
      const {
        data: sponsorship
      } = await supabase.from('sponsorships').select('*').eq('pta_id', data.id).eq('status', 'active').order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      setSponsorshipData(sponsorship);
    }
  };
  const fetchStripeAccount = async () => {
    setLoadingStripe(true);

    // Get user's PTA ID
    const {
      data: memberData
    } = await supabase.from('members').select('pta_id').eq('user_id', user?.id).eq('is_pta_admin', true).single();
    if (!memberData) {
      setLoadingStripe(false);
      return;
    }
    const {
      data,
      error
    } = await supabase.from('pta_stripe_accounts').select('*').eq('pta_id', memberData.pta_id).single();
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching Stripe account:', error);
    } else if (data) {
      setStripeAccount(data);
    }
    setLoadingStripe(false);
  };
  const handleConnectStripe = async () => {
    if (!stripeAccountId.trim()) {
      toast({
        title: "Account ID required",
        description: "Please enter your Stripe account ID",
        variant: "destructive"
      });
      return;
    }
    setConnectingStripe(true);

    // Get user's PTA ID
    const {
      data: memberData
    } = await supabase.from('members').select('pta_id').eq('user_id', user?.id).eq('is_pta_admin', true).single();
    if (!memberData) {
      toast({
        title: "Error",
        description: "Could not find your PTA",
        variant: "destructive"
      });
      setConnectingStripe(false);
      return;
    }
    const {
      error
    } = await supabase.from('pta_stripe_accounts').insert({
      pta_id: memberData.pta_id,
      stripe_account_id: stripeAccountId.trim(),
      stripe_account_status: 'pending',
      onboarding_completed: false
    });
    if (error) {
      toast({
        title: "Connection failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: "Stripe account connected! You can now accept payments."
      });
      fetchStripeAccount();
      setStripeAccountId("");
    }
    setConnectingStripe(false);
  };
  const handleDisconnectStripe = async () => {
    if (!confirm("Are you sure you want to disconnect your Stripe account? This will disable payment processing.")) {
      return;
    }
    const {
      error
    } = await supabase.from('pta_stripe_accounts').delete().eq('id', stripeAccount.id);
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Disconnected",
        description: "Stripe account has been disconnected"
      });
      setStripeAccount(null);
    }
  };
  const getStripeStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'restricted':
        return <Badge variant="destructive">Restricted</Badge>;
      default:
        return null;
    }
  };
  const fetchSponsorshipData = async () => {
    if (!ptaData?.id) return;
    const {
      data
    } = await supabase.from('sponsorships').select('*').eq('pta_id', ptaData.id).eq('status', 'active').order('created_at', {
      ascending: false
    }).limit(1).maybeSingle();
    setSponsorshipData(data);
  };
  const generateNewPhrase = async () => {
    const words = ['happy', 'sunny', 'bright', 'cheerful', 'joyful', 'peaceful', 'lovely', 'gentle', 'rainbow', 'sunshine', 'butterfly', 'garden', 'meadow', 'forest', 'ocean', 'mountain', 'magic', 'wonder', 'sparkle', 'dream', 'harmony', 'melody', 'rhythm', 'dance'];
    const randomWords = Array.from({
      length: 3
    }, () => words[Math.floor(Math.random() * words.length)]);
    const newPhrase = randomWords.join('-');
    const {
      error
    } = await supabase.from('ptas').update({
      three_word_phrase: newPhrase
    }).eq('id', ptaData?.id);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to generate new phrase",
        variant: "destructive"
      });
    } else {
      setThreeWordPhrase(newPhrase);
      toast({
        title: "Phrase regenerated",
        description: `New phrase: ${newPhrase} (old phrase is now invalid)`
      });
    }
  };
  const getPlanPrice = (plan: string) => {
    if (plan === 'basic') return '£0 / year';
    if (plan === 'standard') return '£300 / year';
    return '£0 / year';
  };
  const handleSaveSettings = async () => {
    if (!ptaData?.id) return;
    
    try {
      const { error } = await supabase
        .from('ptas')
        .update({
          show_current_goals: showCurrentGoals,
          allow_public_submissions: allowPublicSubmissions,
          show_pta_members: showPtaMembers,
          show_contact_details: showContactDetails,
          about_text: aboutText,
          volunteer_text: volunteerText,
          hero_welcome_text: heroWelcomeText,
          hero_description_text: heroDescriptionText,
          show_marketplace_section: showMarketplaceSection,
          show_meet_team_section: showMeetTeamSection,
          fundraising_settings_confirmed: true,
        })
        .eq('id', ptaData.id);
      
      if (error) throw error;
      
      // Update local state so checklist reflects the change
      setPtaData({ ...ptaData, fundraising_settings_confirmed: true });
      
      toast({
        title: "Settings saved",
        description: "Fundraising page settings have been updated."
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleConnectDrive = () => {
    connectGoogle('/pta-admin?tab=documents');
  };
  const handleDownloadInvoice = (year: number) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.text("PTA Hub Invoice", 20, 20);

    // PTA Info
    doc.setFontSize(10);
    doc.text("Hillside Primary PTA", 20, 35);
    doc.text("Invoice Date: " + new Date().toLocaleDateString(), 20, 42);
    doc.text("Invoice Year: " + year, 20, 49);

    // Invoice Details
    const cost = year >= 2024 ? 349.00 : 299.00;
    autoTable(doc, {
      startY: 60,
      head: [['Description', 'Amount']],
      body: [['PTA Hub Subscription - Standard Plan', `£${cost.toFixed(2)}`], ['VAT (20%)', `£${(cost * 0.2).toFixed(2)}`], ['Total', `£${(cost * 1.2).toFixed(2)}`]],
      theme: 'striped'
    });

    // Footer
    doc.setFontSize(8);
    doc.text("Thank you for using PTA Hub", 20, doc.internal.pageSize.height - 20);
    doc.text("For support, contact: support@ptahub.com", 20, doc.internal.pageSize.height - 15);
    doc.save(`inv-${year}.pdf`);
    toast({
      title: "Invoice Downloaded",
      description: `PDF invoice for ${year} has been saved`
    });
  };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<any>(null);
  const [transferMemberId, setTransferMemberId] = useState<string>("");
  const [showTransferOption, setShowTransferOption] = useState(false);
  const handleDeleteMember = async (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    // Check if current user is trying to delete themselves
    if (member.user_id === user?.id) {
      toast({
        title: "Cannot delete yourself",
        description: "PTA Admins cannot delete their own account.",
        variant: "destructive"
      });
      return;
    }
    setMemberToDelete(member);
    setShowTransferOption(false);
    setTransferMemberId("");
    setDeleteDialogOpen(true);
  };
  const confirmDelete = async (withTransfer: boolean = false) => {
    if (!memberToDelete) return;
    if (withTransfer) {
      // Show transfer option UI
      setShowTransferOption(true);
      return;
    }

    // If transfer member selected, update related records
    if (transferMemberId) {
      // Transfer tasks
      await supabase.from("tasks").update({
        assigned_to: transferMemberId
      }).eq("assigned_to", memberToDelete.user_id);
    }

    // Delete member (only member record is deleted)
    const {
      error
    } = await supabase.from("members").delete().eq("id", memberToDelete.id);
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: `Member deleted successfully${transferMemberId ? ". Tasks transferred." : "."}`
      });
      fetchMembers();
    }
    setDeleteDialogOpen(false);
    setMemberToDelete(null);
    setTransferMemberId("");
    setShowTransferOption(false);
  };
  const handleMakePTAAdmin = async (memberId: string, currentStatus: boolean) => {
    // Count current PTA admins
    const currentAdminCount = members.filter(m => m.is_pta_admin).length;
    if (!currentStatus && currentAdminCount >= 3) {
      toast({
        title: "Limit reached",
        description: "Maximum of 3 PTA admins per PTA.",
        variant: "destructive"
      });
      return;
    }
    const {
      error
    } = await supabase.from("members").update({
      is_pta_admin: !currentStatus
    }).eq("id", memberId);
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: `Member ${!currentStatus ? "granted" : "removed"} PTA admin status`
      });
      fetchMembers();
    }
  };
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('handleLogoChange called, file:', file?.name);
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG or JPG image",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }
    console.log('Setting logoFile and logoRemoved=false');
    setLogoFile(file);
    setLogoRemoved(false); // Reset the removed flag since we're uploading a new one

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log('FileReader loaded, setting logoPreview');
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveLogo = async () => {
    if (ptaData?.logo_url) {
      // Delete from storage
      const path = ptaData.logo_url.split('/').pop();
      if (path) {
        await supabase.storage.from('pta-logos').remove([`${ptaData.id}/${path}`]);
      }
    }

    // Update database
    const { error } = await supabase.from('ptas').update({
      logo_url: null
    }).eq('id', ptaData?.id);
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to remove logo",
        variant: "destructive"
      });
    } else {
      setLogoFile(null);
      setLogoPreview(null);
      setLogoRemoved(true);
      setPtaData({
        ...ptaData,
        logo_url: null
      });
      toast({
        title: "Success",
        description: "Logo removed successfully"
      });
    }
  };
  const handleSaveGeneralSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ptaData?.id) {
      toast({
        title: "Error",
        description: "PTA data not loaded. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }
    setIsUploadingLogo(true);
    try {
      // Check if logo was explicitly removed
      let logoUrl = logoRemoved ? null : ptaData?.logo_url;
      console.log('handleSaveGeneralSettings - logoRemoved:', logoRemoved, 'logoFile:', logoFile?.name, 'initial logoUrl:', logoUrl);

      // Upload logo if a new file is selected
      if (logoFile) {
        console.log('Uploading new logo file:', logoFile.name);
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `${ptaData.id}/${fileName}`;

        // Delete old logo if exists
        if (ptaData?.logo_url) {
          const oldPath = ptaData.logo_url.split('/').pop();
          if (oldPath) {
            await supabase.storage.from('pta-logos').remove([`${ptaData.id}/${oldPath}`]);
          }
        }
        const {
          error: uploadError
        } = await supabase.storage.from('pta-logos').upload(filePath, logoFile);
        console.log('Upload result - error:', uploadError);
        if (uploadError) throw uploadError;
        const {
          data: {
            publicUrl
          }
        } = supabase.storage.from('pta-logos').getPublicUrl(filePath);
        logoUrl = publicUrl;
        console.log('New logo URL:', logoUrl);
      }

      // Update PTA data
      const formData = new FormData(e.target as HTMLFormElement);
      const {
        error
      } = await supabase.from('ptas').update({
        name: formData.get('pta-name') as string,
        school_name: formData.get('school-name') as string,
        charity_number: formData.get('charity-number') as string,
        county: formData.get('county') as string,
        shared_email: formData.get('shared-email') as string,
        logo_url: logoUrl
      }).eq('id', ptaData?.id);
      if (error) throw error;
      toast({
        title: "Settings saved",
        description: "Your PTA information has been updated successfully."
      });
      fetchPTAData();
      setLogoFile(null);
      setLogoRemoved(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Show loading state while trial status is being determined
  // This prevents the flash of unauthorized content
  if (trialLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-foreground">PTA Admin</h1>
          <p className="text-muted-foreground mt-2">Manage PTA settings and operations</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">PTA Admin</h1>
        <p className="text-muted-foreground mt-2">Manage PTA settings and operations</p>
      </div>

      {/* Trial Expired Banner */}
      {isTrialExpired && (
        <Alert className="border-destructive/50 bg-destructive/10">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <AlertDescription className="ml-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-destructive">Your trial has ended</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add a paid plan to continue using PTA Hub. 
                  {daysUntilDeletion !== null && daysUntilDeletion > 0 && (
                    <span className="font-medium"> Your account will be deleted in {daysUntilDeletion} days.</span>
                  )}
                </p>
              </div>
              <Button asChild variant="default">
                <Link to="/pricing">View Plans</Link>
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          {isTrialExpired ? (
            <TabsTrigger value="subscription">Subscriptions</TabsTrigger>
          ) : (
            <>
              <TabsTrigger value="summary">Summary</TabsTrigger>
              {hasPta && (
                <>
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="fundraising">Fundraising Portal</TabsTrigger>
                  <TabsTrigger value="sponsorship">Sponsorship</TabsTrigger>
                  <TabsTrigger value="payments">Payments</TabsTrigger>
                  <TabsTrigger value="integrations">Integrations</TabsTrigger>
                  <TabsTrigger value="billing">Billing</TabsTrigger>
                  <TabsTrigger value="subscription">Subscriptions</TabsTrigger>
                </>
              )}
            </>
          )}
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          {hasPta && (
          <div className="grid gap-4 md:grid-cols-2">
            {/* PTA Information Card */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>PTA Information</CardTitle>
                <CardDescription>Your PTA details at a glance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">PTA Name</p>
                  <p className="text-lg font-semibold">{ptaData?.name || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">School Name</p>
                  <p className="text-lg">{ptaData?.school_name || 'Not set'}</p>
                </div>
                {ptaData?.charity_number && <div>
                    <p className="text-sm font-medium text-muted-foreground">Charity Number</p>
                    <p className="text-lg">{ptaData.charity_number}</p>
                  </div>}
                {ptaData?.county && <div>
                    <p className="text-sm font-medium text-muted-foreground">County</p>
                    <p className="text-lg">{ptaData.county}</p>
                  </div>}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Sponsorship Code</p>
                  <p className="text-lg font-mono">{ptaData?.sponsorship_code || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Members</p>
                  <p className="text-lg">{members.length} total</p>
                </div>
              </CardContent>
            </Card>

            {/* Onboarding Checklist Card */}
            {!checklistHidden ? (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Getting Started Checklist</CardTitle>
                <CardDescription>Complete these steps to get your PTA up and running</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  {ptaData?.logo_url ? <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <p className="font-medium text-sm">Upload PTA Logo</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Add your PTA logo to personalize your page
                    </p>
                    <Button variant="link" size="sm" className="h-auto p-0 mt-1" onClick={() => setActiveTab('general')}>
                      Go to General Settings →
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  {members.length > 1 ? <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <p className="font-medium text-sm">Invite your first member</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {members.length > 1 ? `${members.length - 1} member${members.length > 2 ? 's' : ''} invited` : 'Build your PTA team by inviting members'}
                    </p>
                    {members.length === 1 && <Button variant="link" size="sm" className="h-auto p-0 mt-1" onClick={() => setActiveTab('members')}>
                        Go to Members →
                      </Button>}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  {ptaData?.fundraising_settings_confirmed ? <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <p className="font-medium text-sm">Confirm fundraising page settings</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Review and confirm your public fundraising page settings
                    </p>
                    <Button variant="link" size="sm" className="h-auto p-0 mt-1" onClick={() => setActiveTab('fundraising')}>
                      Go to Fundraising Settings →
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  {subscriptionData?.status === 'active' ? <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <p className="font-medium text-sm">Add a pricing plan</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {subscriptionData?.status === 'active' ? `${subscriptionData.plan.charAt(0).toUpperCase() + subscriptionData.plan.slice(1)} plan active` : 'Choose a paid subscription plan for your PTA'}
                    </p>
                    {subscriptionData?.status !== 'active' && <Button variant="link" size="sm" className="h-auto p-0 mt-1" onClick={() => setActiveTab('subscription')}>
                        Go to Subscriptions →
                      </Button>}
                  </div>
                </div>

                {ptaData?.logo_url && members.length > 1 && ptaData?.fundraising_settings_confirmed && subscriptionData?.status === 'active' && <div className="pt-2 border-t">
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setChecklistHidden(true)}>
                      Hide Checklist
                    </Button>
                  </div>}
              </CardContent>
            </Card>
            ) : (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>PTA Overview</CardTitle>
                <CardDescription>Your PTA status at a glance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Current Plan</p>
                  <p className="text-lg font-semibold capitalize">{subscriptionData?.plan || 'No plan selected'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Subscription Status</p>
                  <Badge variant={subscriptionData?.status === 'active' ? 'default' : 'secondary'} className="mt-1">
                    {subscriptionData?.status?.charAt(0).toUpperCase() + subscriptionData?.status?.slice(1) || 'Inactive'}
                  </Badge>
                </div>
                <div className="pt-2 border-t">
                  <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setChecklistHidden(false)}>
                    Show Checklist
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </div>
          )}
        </TabsContent>

        <TabsContent value="members" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>PTA Members</CardTitle>
                  <CardDescription>Manage your PTA team members and their roles</CardDescription>
                </div>
                <Button onClick={() => setInviteModalOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {members.map(member => <div key={member.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.full_name}</p>
                        {member.is_pta_admin && <Badge variant="default" className="text-xs">
                            PTA Admin
                          </Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                      {member.user_roles && member.user_roles.length > 0 && <div className="flex flex-wrap gap-1 mt-2">
                          {member.user_roles.map((r: any) => (
                            <Badge key={r.role} variant="outline" className="text-xs">
                              {r.role}
                            </Badge>
                          ))}
                        </div>}
                    </div>
                    <div className="flex items-center gap-2">
                      {!member.is_pta_admin && <Button size="sm" variant="outline" onClick={() => handleMakePTAAdmin(member.id, false)}>
                          Make Admin
                        </Button>}
                      {member.is_pta_admin && member.user_id !== user?.id && <Button size="sm" variant="outline" onClick={() => handleMakePTAAdmin(member.id, true)}>
                          Remove Admin
                        </Button>}
                      <Button size="sm" variant="ghost" onClick={() => {
                    setMemberToEdit(member);
                    setEditDialogOpen(true);
                  }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteMember(member.id)} disabled={member.user_id === user?.id}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>)}
                {members.length === 0 && <p className="text-center text-muted-foreground py-8">
                    No members yet. Click "Invite Member" to add your first member.
                  </p>}
              </div>
            </CardContent>
          </Card>
          
          <MemberInviteModal open={inviteModalOpen} onOpenChange={setInviteModalOpen} onSuccess={fetchMembers} />
          
          <EditMemberDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} member={memberToEdit} onSuccess={fetchMembers} />

          {/* Delete Member Dialog with Transfer Option */}
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Member</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete {memberToDelete?.full_name}?
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Deleting this member will remove their role from the PTA. You can optionally transfer their tasks to another member before deletion.
                </p>
                
                {showTransferOption && <div className="space-y-2">
                    <Label>Transfer tasks to</Label>
                    <Select value={transferMemberId} onValueChange={setTransferMemberId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select member" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.filter(m => m.id !== memberToDelete?.id).map(m => <SelectItem key={m.id} value={m.user_id}>
                              {m.full_name}
                            </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>}
                
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => {
                  setDeleteDialogOpen(false);
                  setShowTransferOption(false);
                  setTransferMemberId("");
                }}>
                    Cancel
                  </Button>
                  {!showTransferOption ? <>
                      <Button variant="destructive" onClick={() => confirmDelete(false)}>
                        Delete Member
                      </Button>
                      <Button variant="default" onClick={() => confirmDelete(true)}>
                        Transfer Member
                      </Button>
                    </> : <Button variant="destructive" onClick={() => confirmDelete(false)} disabled={!transferMemberId}>
                      Confirm Transfer & Delete
                    </Button>}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="general" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>PTA Information</CardTitle>
              <CardDescription>
                Basic details about your PTA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleSaveGeneralSettings}>
                <div className="space-y-2">
                  <Label htmlFor="pta-name">PTA Name</Label>
                  <Input id="pta-name" name="pta-name" placeholder="Hillside Primary PTA" defaultValue={ptaData?.name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school-name">School Name</Label>
                  <Input id="school-name" name="school-name" placeholder="Hillside Primary School" defaultValue={ptaData?.school_name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="charity-number">Charity Number</Label>
                  <Input id="charity-number" name="charity-number" placeholder="123456" defaultValue={ptaData?.charity_number} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Input id="county" name="county" placeholder="e.g., Hampshire" defaultValue={ptaData?.county} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shared-email">Shared Email (Optional)</Label>
                  <Input id="shared-email" name="shared-email" type="email" placeholder="pta@school.com" defaultValue={ptaData?.shared_email} />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="pta-logo">PTA Logo</Label>
                  <p className="text-xs text-muted-foreground">
                    Upload a PNG or JPG image (max 5MB)
                  </p>
                  
                  {logoPreview ? <div className="space-y-3">
                      <div className="relative w-40 h-40 border border-border rounded-lg overflow-hidden bg-muted">
                        <img src={logoPreview} alt="PTA Logo Preview" className="w-full h-full object-contain" />
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                          <Upload className="h-4 w-4 mr-2" />
                          Change Logo
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={handleRemoveLogo}>
                          <X className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    </div> : <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()}>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Logo
                      </Button>
                    </div>}
                  
                  <input 
                    ref={logoInputRef}
                    type="file" 
                    accept="image/png,image/jpeg,image/jpg" 
                    className="hidden" 
                    onChange={handleLogoChange} 
                  />
                </div>

                <Button type="submit" disabled={isUploadingLogo}>
                  <Save className="h-4 w-4 mr-2" />
                  {isUploadingLogo ? "Saving..." : "Save Changes"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fundraising" className="space-y-4">
          {/* Fundraising Page Link */}
          <Card className="shadow-card border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Public Fundraising Page</CardTitle>
                  <CardDescription>Share this link with parents and the community</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Fundraising Page URL</Label>
                  <div className="flex gap-2">
                    <Input 
                      readOnly 
                      value={`ptahub.co.uk/fundraising/${ptaData?.id}`} 
                      className="font-mono text-sm" 
                    />
                    <Button variant="outline" onClick={() => {
                      navigator.clipboard.writeText(`https://ptahub.co.uk/fundraising/${ptaData?.id}`);
                      toast({
                        title: "Link copied",
                        description: "Fundraising page link copied to clipboard"
                      });
                    }}>
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    This is your public fundraising page that anyone can view. Parents need to register with the 3-word phrase to donate or post to the marketplace.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3-Word Phrase for Parent Registration */}
          <Card className="shadow-card border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Parent Registration Phrase</CardTitle>
                  <CardDescription>Parents need this phrase to register and access features</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Current 3-Word Phrase</Label>
                  <div className="flex gap-2 items-center">
                    <code className="px-3 py-2 bg-muted rounded font-mono text-sm flex-1">
                      {threeWordPhrase}
                    </code>
                    <Button variant="outline" onClick={() => {
                      navigator.clipboard.writeText(threeWordPhrase);
                      toast({
                        title: "Phrase copied",
                        description: "3-word phrase copied to clipboard"
                      });
                    }}>
                      Copy
                    </Button>
                  </div>
                  <Button variant="destructive" size="sm" className="mt-3 w-full" onClick={generateNewPhrase}>
                    Generate New Phrase (Voids Old One)
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    ⚠️ Regenerating will invalidate the old phrase for all parents
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fundraising Page Settings */}
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Fundraising Page Settings</CardTitle>
                  <CardDescription>Configure your public fundraising page</CardDescription>
                </div>
                
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="about-text">About Our PTA</Label>
                  <Textarea 
                    id="about-text" 
                    placeholder="Tell visitors about your PTA..." 
                    rows={6} 
                    value={aboutText}
                    onChange={(e) => setAboutText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">This text will appear in the About section of your fundraising page</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="volunteer-text">How to Volunteer/Become a Member</Label>
                  <Textarea 
                    id="volunteer-text" 
                    placeholder="Explain how people can get involved with your PTA..." 
                    rows={6} 
                    value={volunteerText}
                    onChange={(e) => setVolunteerText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">This text will appear on the volunteer/membership page</p>
                </div>
                
                <Separator className="my-6" />
                
                <h3 className="font-semibold text-lg mb-4">Hero Section Text</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="hero-welcome-text">Welcome Heading</Label>
                  <Input 
                    id="hero-welcome-text" 
                    placeholder="Welcome to Our School" 
                    value={heroWelcomeText}
                    onChange={(e) => setHeroWelcomeText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">The main heading text in the hero section</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="hero-description-text">Hero Description</Label>
                  <Textarea 
                    id="hero-description-text" 
                    placeholder="Join our community in creating amazing opportunities..." 
                    rows={4} 
                    value={heroDescriptionText}
                    onChange={(e) => setHeroDescriptionText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">The description text below the heading in the hero section</p>
                </div>
                
                <Separator className="my-6" />
                
                <h3 className="font-semibold text-lg mb-4">Section Visibility</h3>
                
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Show Current Goals</p>
                    <p className="text-sm text-muted-foreground">Display fundraising goals on public page</p>
                  </div>
                  <Switch 
                    checked={showCurrentGoals} 
                    onCheckedChange={setShowCurrentGoals}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Allow Public Submissions</p>
                    <p className="text-sm text-muted-foreground">Let community submit fundraising ideas</p>
                  </div>
                  <Switch 
                    checked={allowPublicSubmissions} 
                    onCheckedChange={setAllowPublicSubmissions}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Show PTA Members</p>
                    <p className="text-sm text-muted-foreground">Display team member profiles</p>
                  </div>
                  <Switch 
                    checked={showPtaMembers} 
                    onCheckedChange={setShowPtaMembers}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Show Contact Details</p>
                    <p className="text-sm text-muted-foreground">Display PTA contact information</p>
                  </div>
                  <Switch 
                    checked={showContactDetails} 
                    onCheckedChange={setShowContactDetails}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Show Marketplace Section</p>
                    <p className="text-sm text-muted-foreground">Display marketplace items on fundraising page</p>
                  </div>
                  <Switch 
                    checked={showMarketplaceSection} 
                    onCheckedChange={setShowMarketplaceSection}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Show Meet the Team</p>
                    <p className="text-sm text-muted-foreground">Display team member profiles on fundraising page</p>
                  </div>
                  <Switch 
                    checked={showMeetTeamSection} 
                    onCheckedChange={setShowMeetTeamSection}
                  />
                </div>

                <Separator className="my-6" />
                
                {/* Header Customization */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Header Customization</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="header-color">Header Color</Label>
                    <Select defaultValue="#6366f1">
                      <SelectTrigger id="header-color">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="#6366f1">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#6366f1'
                          }}></div>
                            Indigo
                          </div>
                        </SelectItem>
                        <SelectItem value="#8b5cf6">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#8b5cf6'
                          }}></div>
                            Purple
                          </div>
                        </SelectItem>
                        <SelectItem value="#ec4899">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#ec4899'
                          }}></div>
                            Pink
                          </div>
                        </SelectItem>
                        <SelectItem value="#14b8a6">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#14b8a6'
                          }}></div>
                            Teal
                          </div>
                        </SelectItem>
                        <SelectItem value="#f59e0b">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#f59e0b'
                          }}></div>
                            Amber
                          </div>
                        </SelectItem>
                        <SelectItem value="#ef4444">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{
                            backgroundColor: '#ef4444'
                          }}></div>
                            Red
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logo-upload">PTA Logo</Label>
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                          <span className="text-2xl">🏫</span>
                        </div>
                        <div className="flex-1">
                          <Input id="logo-upload" type="file" accept="image/*" />
                          <p className="text-xs text-muted-foreground mt-1">PNG, JPG or SVG (max 2MB)</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logo-position">Logo Position</Label>
                    <Select defaultValue="left">
                      <SelectTrigger id="logo-position">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleSaveSettings} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* FAQ Manager */}
          {ptaData?.id && <FAQManager ptaId={ptaData.id} />}

          {/* Social Links Manager */}
          {ptaData?.id && <SocialLinksManager ptaId={ptaData.id} />}
        </TabsContent>

        <TabsContent value="sponsorship" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Membership Sponsorship</CardTitle>
              <CardDescription>
                Get your PTA membership sponsored by local businesses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {sponsorshipData ? <div className="space-y-4">
                    <div className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-2 border-primary/20 rounded-lg">
                      <h3 className="font-semibold text-lg mb-4">Active Sponsorship</h3>
                      <div className="space-y-2">
                        <p className="text-sm">
                          <span className="font-medium">Sponsored by:</span> {sponsorshipData.business_name}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Contact:</span> {sponsorshipData.contact_first_name} {sponsorshipData.contact_last_name}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Email:</span> {sponsorshipData.contact_email}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Plan:</span> {sponsorshipData.sponsored_plan === 'basic' ? 'Basic' : 'Standard'}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Valid until:</span> {new Date(sponsorshipData.end_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Show on fundraising page:</span> {sponsorshipData.show_on_fundraising ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                    <div className="p-4 bg-accent/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Your sponsorship is active and your membership is currently free. 
                        To renew or change sponsors, share your sponsorship code with a new sponsor 
                        2 weeks before the expiration date.
                      </p>
                    </div>
                  </div> : <div className="space-y-4">
                    <div className="p-6 bg-muted/30 rounded-lg">
                      <h3 className="font-semibold mb-3">How Sponsorship Works</h3>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li>• Share your unique 6-digit code with a local business</li>
                        <li>• The business visits the sponsorship page and enters your code</li>
                        <li>• They complete payment to sponsor your membership</li>
                        <li>• Your membership becomes free and their business name is displayed</li>
                      </ul>
                    </div>

                    <div className="p-6 border-2 border-primary/20 rounded-lg bg-gradient-to-br from-primary/5 to-transparent">
                      <Label className="text-sm font-medium mb-2 block">Your Sponsorship Code</Label>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex gap-2">
                          {(ptaData?.sponsorship_code || '000000').split('').map((digit: string, i: number) => <div key={i} className="w-12 h-14 flex items-center justify-center text-2xl font-bold border-2 border-primary rounded-lg bg-background">
                              {digit}
                            </div>)}
                        </div>
                        <Button variant="outline" onClick={() => {
                      if (ptaData?.sponsorship_code) {
                        navigator.clipboard.writeText(ptaData.sponsorship_code);
                        toast({
                          title: "Code copied",
                          description: "Sponsorship code copied to clipboard"
                        });
                      }
                    }}>
                          Copy Code
                        </Button>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Share this code with potential sponsors. They can enter it at:{' '}
                        <a href="/sponsorship" className="text-primary hover:underline">
                          {window.location.origin}/sponsorship
                        </a>
                      </p>
                    </div>

                    <div className="p-4 bg-accent/30 rounded-lg">
                      <p className="text-sm font-medium mb-2">Benefits for Sponsors:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Business name displayed on your PTA dashboard</li>
                        <li>• Optional visibility on your fundraising page</li>
                        <li>• Support local education and community</li>
                        <li>• Tax-deductible charitable contribution</li>
                      </ul>
                    </div>
                  </div>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Payment Settings</CardTitle>
              <CardDescription>
                Connect your Stripe account to accept payments for credit purchases and marketplace transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingStripe ? <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div> : stripeAccount ? <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    {getStripeStatusBadge(stripeAccount.stripe_account_status)}
                  </div>
                  <div className="grid gap-2">
                    <Label>Account ID</Label>
                    <div className="flex items-center gap-2">
                      <Input value={`****${stripeAccount.stripe_account_id.slice(-4)}`} readOnly className="flex-1" />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label>Onboarding Status</Label>
                    <div className="text-sm">
                      {stripeAccount.onboarding_completed ? <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> Completed
                        </span> : <span className="text-orange-600">Incomplete</span>}
                    </div>
                  </div>

                  <div className="p-4 bg-accent/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      This Stripe account will receive all credit purchase payments from parents in your marketplace.
                    </p>
                  </div>

                  <Button variant="destructive" onClick={handleDisconnectStripe} className="w-full">
                    Disconnect Stripe Account
                  </Button>
                </div> : <div className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="stripe-account">Stripe Account ID</Label>
                    <Input id="stripe-account" value={stripeAccountId} onChange={e => setStripeAccountId(e.target.value)} placeholder="acct_..." />
                    <p className="text-xs text-muted-foreground">
                      Find this in your Stripe Dashboard under Settings → Account details.{" "}
                      <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Create a Stripe account
                      </a>
                    </p>
                  </div>

                  <div className="p-4 bg-accent/30 rounded-lg">
                    <p className="text-sm font-medium mb-2">By connecting your Stripe account, you'll be able to:</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Accept credit purchases from parents</li>
                      <li>• Receive payments directly to your account</li>
                      <li>• Enable Stripe payment method in shop settings</li>
                    </ul>
                  </div>

                  <Button onClick={handleConnectStripe} disabled={connectingStripe || !stripeAccountId.trim()} className="w-full">
                    {connectingStripe ? "Connecting..." : "Connect Stripe Account"}
                  </Button>
                </div>}
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Payment Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <h3 className="font-semibold mb-2">How payments work:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Parents purchase credits through Stripe, funds go directly to your PTA account</li>
                  <li>• Parents spend credits to list items in the marketplace</li>
                  <li>• PTA items can use Stripe checkout for direct purchases</li>
                  <li>• Parent-to-parent sales are handled directly, no platform fees</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>



        <TabsContent value="integrations" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>Connect third-party services to enhance your PTA experience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Integrations Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Google Integrations</h3>
                  {subscriptionData?.plan !== 'standard' && !subscriptionData?.trial_started_at && (
                    <Badge variant="secondary">Standard Plan Required</Badge>
                  )}
                </div>
                
                {subscriptionData?.plan !== 'standard' && !subscriptionData?.trial_started_at ? (
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-3">
                      Google integrations are available on the Standard plan. Upgrade to connect your Google account and enable Calendar, Meet, and Drive features.
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/pricing">Upgrade to Standard</Link>
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Connection Status / Connect Button */}
                    <div className="p-4 bg-muted/30 rounded-lg border border-border">
                      {googleLoading ? (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Loading Google connection...</span>
                        </div>
                      ) : googleConnected ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                            <span className="text-sm">Connected as <strong>{googleEmail}</strong></span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={disconnectGoogle}
                          >
                            Disconnect Google
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">
                            Connect your Google account to enable Calendar, Meet, and Drive integrations.
                          </p>
                          <Button
                            onClick={() => connectGoogle()}
                            disabled={googleConnecting}
                          >
                            {googleConnecting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              'Connect to Google'
                            )}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Google Calendar Card */}
                    <div className="p-4 border border-border rounded-lg">
                      <div className="flex items-start gap-4">
                        <img src={googleCalendarLogo} alt="Google Calendar" className="w-10 h-10" />
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">Google Calendar</h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            Automatically sync PTA meetings and events to your connected Google Calendar.
                          </p>
                          {googleConnected ? (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={googleCalendarEnabled}
                                onCheckedChange={toggleCalendarEnabled}
                              />
                              <Label className="text-sm">Enable Calendar sync</Label>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Connect your Google account above to enable</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Google Meet Card */}
                    <div className="p-4 border border-border rounded-lg">
                      <div className="flex items-start gap-4">
                        <img src={googleMeetLogo} alt="Google Meet" className="w-[37px] h-[37px]" />
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">Google Meet</h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            Automatically create Google Meet video conference links when scheduling meetings.
                          </p>
                          {googleConnected ? (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={googleMeetEnabled}
                                onCheckedChange={toggleMeetEnabled}
                              />
                              <Label className="text-sm">Enable Meet links</Label>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Connect your Google account above to enable</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Google Drive Card */}
                    <div className="p-4 border border-border rounded-lg">
                      <div className="flex items-start gap-4">
                        <img src={googleDriveLogo} alt="Google Drive" className="w-12 h-12" />
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">Google Drive</h4>
                          <p className="text-sm text-muted-foreground mb-3">
                            Store and share PTA documents directly in Google Drive for easy collaboration.
                          </p>
                          {googleConnected ? (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={googleDriveEnabled}
                                onCheckedChange={toggleDriveEnabled}
                              />
                              <Label className="text-sm">Enable Drive integration</Label>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Connect your Google account above to enable</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Billing</CardTitle>
                  <CardDescription>View your billing history</CardDescription>
                </div>
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {subscriptionData?.stripe_customer_id && (
                  <div className="p-4 border border-border rounded-lg">
                    <h4 className="font-semibold mb-2">Payment Method</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      {subscriptionData?.stripe_payment_intent_id ? 'Payment method on file' : 'No payment method'}
                    </p>
                    <Button variant="outline" className="w-full">Update Payment Method</Button>
                  </div>
                )}
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-semibold mb-2">Billing History</h4>
                  {invoices.length > 0 ? (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className="flex items-center justify-between text-sm py-2 border-b">
                          <div className="flex items-center gap-4 flex-1">
                            <span className="font-medium w-32">{new Date(invoice.renewal_date).toLocaleDateString()}</span>
                            <span className="text-muted-foreground">{invoice.invoice_number}</span>
                            <span className="font-medium">£{invoice.amount}</span>
                            <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                              {invoice.status}
                            </Badge>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => {
                              // Download invoice logic here
                              toast({
                                title: "Invoice Download",
                                description: "Invoice download will be available soon"
                              });
                            }}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">No invoices yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Subscription</CardTitle>
                  <CardDescription>Manage your PTA Hub subscription and plan</CardDescription>
                </div>
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Plan Status */}
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Current Plan</h4>
                  <Badge variant={trialState === 'paid_active' ? 'default' : trialState === 'active_trial' ? 'outline' : 'destructive'}>
                    {trialState === 'paid_active' ? 'Active' : trialState === 'active_trial' ? 'Trial' : 'Expired'}
                  </Badge>
                </div>
                {/* Show "Standard Plan Trial" for trial users to clarify they had Standard features */}
                <p className="text-2xl font-bold capitalize">
                  {subscriptionData?.trial_started_at 
                    ? 'Standard Plan Trial'
                    : subscriptionData?.plan 
                      ? `${subscriptionData.plan} Plan`
                      : 'No Plan Selected'}
                </p>
                {/* Only show price if they were/are on a paid plan (no trial_started_at) */}
                {!subscriptionData?.trial_started_at && subscriptionData?.plan && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {getPlanPrice(subscriptionData?.plan || 'basic')}
                  </p>
                )}
                
                {/* Trial Days Remaining */}
                {trialState === 'active_trial' && daysRemaining !== null && (
                  <div className="mt-3 p-3 bg-primary/10 rounded-md">
                    <p className="text-sm font-medium text-primary">
                      {daysRemaining} days remaining in your trial
                    </p>
                  </div>
                )}
              </div>

              {/* Plan Selection from Database */}
              <div>
                <h4 className="font-semibold mb-4">Choose a Plan</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {pricingPlans.map((plan) => {
                    const previousPlan = subscriptionData?.plan?.toLowerCase();
                    const planNameLower = plan.name.toLowerCase();
                    
                    // User was on a FREE trial if trial_started_at exists
                    const wasOnFreeTrial = !!subscriptionData?.trial_started_at;
                    
                    // User was a PAID subscriber if they never had a trial period
                    const wasPaidSubscriber = !wasOnFreeTrial && previousPlan && (previousPlan === 'basic' || previousPlan === 'standard');
                    
                    const isCurrentPlan = trialState === 'paid_active' && previousPlan === planNameLower;
                    
                    // For trial users: only show as selected if they clicked in THIS session
                    // For paid subscribers: use the database value
                    const isSelectedPlan = wasOnFreeTrial
                      ? localSelectedPlan === planNameLower
                      : subscriptionData?.plan_selected?.toLowerCase() === planNameLower;
                    
                    const features = planFeatures[plan.id] || [];
                    
                    // Determine button text based on user type and selection
                    const getButtonText = () => {
                      // If this plan is selected in this session
                      if (isSelectedPlan && !isCurrentPlan) {
                        return 'Selected Plan';
                      }
                      // If user had a paid plan that expired (not trial)
                      if (!wasOnFreeTrial && wasPaidSubscriber) {
                        // Renew button for the same plan they were on
                        if (previousPlan === planNameLower) {
                          return 'Renew Plan';
                        }
                        // Upgrade/Downgrade for other plans
                        if (previousPlan === 'standard' && planNameLower === 'basic') {
                          return 'Downgrade to Basic';
                        }
                        if (previousPlan === 'basic' && planNameLower === 'standard') {
                          return 'Upgrade to Standard';
                        }
                      }
                      // For trial users, just show Select
                      return `Select ${plan.name}`;
                    };
                    
                    // Determine if the card should have the selected styling
                    const hasSelectedStyling = isCurrentPlan || isSelectedPlan;
                    
                    return (
                      <div 
                        key={plan.id} 
                        className={`p-4 border rounded-lg transition-all ${hasSelectedStyling ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-lg">{plan.name}</h4>
                            {plan.is_popular && (
                              <Badge variant="secondary" className="text-xs">Popular</Badge>
                            )}
                          </div>
                          {isCurrentPlan && (
                            <Badge variant="default">Your Plan</Badge>
                          )}
                        </div>
                        <p className="text-2xl font-bold">
                          £{plan.price}
                          <span className="text-sm font-normal text-muted-foreground">/year</span>
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                        
                        {/* Features List */}
                        <ul className="space-y-2 text-sm mb-4 max-h-48 overflow-y-auto">
                          {features.slice(0, 6).map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                          {features.length > 6 && (
                            <li className="text-muted-foreground text-xs">
                              + {features.length - 6} more features
                            </li>
                          )}
                        </ul>
                        
                        {/* Select Button */}
                        {isSelectedPlan && !isCurrentPlan ? (
                          <Button className="w-full" variant="ghost" disabled>
                            Selected Plan
                          </Button>
                        ) : !isCurrentPlan ? (
                          <Button 
                            className="w-full" 
                            variant="default"
                            onClick={() => handleSelectPlan(plan.name)}
                            disabled={savingPlan}
                          >
                            {savingPlan ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              getButtonText()
                            )}
                          </Button>
                        ) : (
                          <Button className="w-full" variant="ghost" disabled>
                            Current Plan
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Subscribe Button for Trial Users */}
              {(trialState === 'trial_expired' || trialState === 'active_trial') && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-3">
                    Ready to subscribe? Complete your payment to activate your selected plan.
                  </p>
                  <Button 
                    className="w-full sm:w-auto" 
                    onClick={handleCompletePayment}
                    disabled={processingPayment || (!localSelectedPlan && !subscriptionData?.plan_selected)}
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Complete Payment
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>;
}